import { useState, useEffect, useReducer, useRef } from "react";

import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { resolveSessionInvalidatedToast } from "../../utils/sessionInvalidatedMessage";

import { getSocket, onSocket, cleanupComponent } from "../../services/socket";
import api from "../../services/api";

const reducer = (state, action) => {
  const currentState = Array.isArray(state) ? state : [];

  switch (action.type) {
    case "LOAD_WHATSAPPS":
      return Array.isArray(action.payload) ? action.payload : [action.payload].filter(Boolean);
    case "UPDATE_WHATSAPPS":
      try {
        const whatsApp = action.payload;
        if (!whatsApp) return currentState;
        
        const updatedState = [...currentState];
        
        const whatsAppIndex = updatedState.findIndex((s) => s && s.id === whatsApp.id);
        
        if (whatsAppIndex !== -1) {
          updatedState[whatsAppIndex] = {
            ...updatedState[whatsAppIndex],
            ...whatsApp,
          };
          
          if (whatsApp.isDefault && whatsApp.channel) {
            return updatedState.map(item => {
              if (item && typeof item === 'object' && item.id && item.channel) {
                if (item.id !== whatsApp.id && item.channel === whatsApp.channel) {
                  return { ...item, isDefault: false };
                }
              }
              return item;
            });
          }
          
          return updatedState;
        } else {
          return [whatsApp, ...updatedState];
        }
      } catch (error) {
        console.error("Error al actualizar WhatsApp:", error);
        return currentState;
      }
    case "UPDATE_SESSION":
      try {
        const whatsAppSession = action.payload;
        console.log("🔧 REDUCER UPDATE_SESSION - Payload recibido:", whatsAppSession);
        
        if (!whatsAppSession || !whatsAppSession.id) {
          console.warn("⚠️ REDUCER UPDATE_SESSION - Payload inválido, retornando estado actual");
          return currentState;
        }
        
        const updatedState = [...currentState];
        
        const whatsAppSessionIndex = updatedState.findIndex(
          (s) => s && s.id === whatsAppSession.id
        );
        
        console.log("🔍 REDUCER UPDATE_SESSION - Buscando WhatsApp ID:", whatsAppSession.id);
        console.log("🔍 REDUCER UPDATE_SESSION - Índice encontrado:", whatsAppSessionIndex);

        if (whatsAppSessionIndex !== -1) {
          const oldState = updatedState[whatsAppSessionIndex];
          const newState = {
            ...updatedState[whatsAppSessionIndex],
            status: whatsAppSession.status || updatedState[whatsAppSessionIndex].status,
            qrcode: whatsAppSession.qrcode || updatedState[whatsAppSessionIndex].qrcode,
            retries: typeof whatsAppSession.retries === 'number' ? whatsAppSession.retries : updatedState[whatsAppSessionIndex].retries,
            ...(whatsAppSession.qualityRating !== undefined
              ? { qualityRating: whatsAppSession.qualityRating }
              : {}),
            ...(whatsAppSession.messagingLimit !== undefined
              ? { messagingLimit: whatsAppSession.messagingLimit }
              : {}),
            ...(whatsAppSession.cloudPaymentRequired !== undefined
              ? { cloudPaymentRequired: whatsAppSession.cloudPaymentRequired }
              : {}),
          };
          
          console.log("✅ REDUCER UPDATE_SESSION - Estado actualizado:", {
            oldStatus: oldState.status,
            newStatus: newState.status,
            oldQrcodeLength: oldState.qrcode?.length || 0,
            newQrcodeLength: newState.qrcode?.length || 0,
            oldRetries: oldState.retries,
            newRetries: newState.retries
          });
          
          updatedState[whatsAppSessionIndex] = newState;
          return updatedState;
        }
        
        console.warn("⚠️ REDUCER UPDATE_SESSION - WhatsApp no encontrado en el estado actual");
        return currentState;
      } catch (error) {
        console.error("❌ Error al actualizar sesión de WhatsApp:", error);
        return currentState;
      }
    case "DELETE_WHATSAPPS":
      try {
        const whatsAppId = action.payload;
        if (!whatsAppId) return currentState;
        
        const whatsAppList = currentState.filter((s) => s && s.id !== whatsAppId);
        return whatsAppList;
      } catch (error) {
        console.error("Error al eliminar WhatsApp:", error);
        return currentState;
      }
    case "RESET":
      return [];
    default:
      return currentState;
  }
};

const useWhatsApps = () => {
  const [loading, setLoading] = useState(true);
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [lastManualUpdate, setLastManualUpdate] = useState(0);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const lastManualUpdateRef = useRef(lastManualUpdate);

  // Función wrapper para dispatch que también actualiza lastManualUpdate
  const dispatchWithTimestamp = (action) => {
    if (action.type === "LOAD_WHATSAPPS") {
      setLastManualUpdate(Date.now());
    }
    dispatch(action);
  };

  useEffect(() => {
    lastManualUpdateRef.current = lastManualUpdate;
  }, [lastManualUpdate]);

  useEffect(() => {
    setLoading(true);
    const fetchSession = async () => {
      try {
        const companyId = localStorage.getItem("companyId");
        if (!companyId) {
          throw new Error("No se encontró el ID de la compañía");
        }

        const { data } = await api.get(`/whatsapp?session=0&companyId=${companyId}`);
        const safeData = data === null || data === undefined ? [] : 
                        Array.isArray(data) ? data : [data];
        dispatchWithTimestamp({ type: "LOAD_WHATSAPPS", payload: safeData });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        dispatchWithTimestamp({ type: "LOAD_WHATSAPPS", payload: [] });
        toastError(err);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const componentId = `useWhatsApps-${companyId}`;
    getSocket(companyId);

    const onWhatsappUpdate = (data) => {
      const currentTime = Date.now();
      const timeSinceLastManualUpdate = currentTime - lastManualUpdateRef.current;

      if (timeSinceLastManualUpdate < 2000) {
        console.log("Ignorando actualización por update manual reciente");
        return;
      }

      if (data?.whatsapp) {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
      }
    };

    const onWhatsappCreate = (data) => {
      if (data?.whatsapp) {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
      }
    };

    const onWhatsappDelete = (data) => {
      if (data?.whatsappId) {
        dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
      }
    };

    const onWhatsappSession = (data) => {
      console.log("\n🔍 ============ EVENTO SOCKET RECIBIDO EN FRONTEND ============");
      console.log("📥 Evento:", `whatsappSession:update`);
      console.log("📦 Data recibida:", JSON.stringify(data, null, 2));
      console.log("🔍 Detalles de la sesión:", {
        whatsappId: data.session?.id,
        status: data.session?.status,
        qrcodeLength: data.session?.qrcode?.length,
        retries: data.session?.retries,
        timestamp: new Date().toISOString()
      });

      if (data?.session) {
        const invalidatedToast = resolveSessionInvalidatedToast(data.session);
        if (invalidatedToast) {
          toast.warn(invalidatedToast, {
            autoClose: 9000,
            toastId: `session-invalidated-${data.session.id}`,
          });
        }

        console.log("✅ Actualizando sesión en reducer con UPDATE_SESSION");
        dispatch({ type: "UPDATE_SESSION", payload: data.session });
        console.log("🔍 ================================================\n");
      }
    };

    onSocket("whatsapp:update", onWhatsappUpdate, componentId);
    onSocket("whatsapp:create", onWhatsappCreate, componentId);
    onSocket("whatsapp:delete", onWhatsappDelete, componentId);
    onSocket("whatsappSession:update", onWhatsappSession, componentId);

    return () => {
      cleanupComponent(componentId);
    };
  }, []);

  // Efecto para verificar el estado de las conexiones
  useEffect(() => {
    const checkConnections = () => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        
        const hasActiveConnections = whatsApps.some(whats => whats.status === "CONNECTED");
        
        if (offlineWhats.length === whatsApps.length || !hasActiveConnections) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    };

    const interval = setInterval(checkConnections, 2000);
    return () => clearInterval(interval);
  }, [whatsApps]);

  return { whatsApps, loading, dispatch: dispatchWithTimestamp, connectionWarning };
};

export default useWhatsApps;
